import { useState, useEffect } from 'react';
import { plegar, coincide } from './busqueda.logic.js';
// GEOBAJA Utilities

const fmt = {
  money: n => n == null ? "—" : new Intl.NumberFormat("es-MX",{style:"currency",currency:"MXN",minimumFractionDigits:2,maximumFractionDigits:2}).format(n),
  moneyFull: n => n == null ? "—" : new Intl.NumberFormat("es-MX",{style:"currency",currency:"MXN"}).format(n),
  date: s => s ? new Date(s+"T12:00:00").toLocaleDateString("es-MX",{day:"2-digit",month:"short",year:"numeric"}) : "—",
  dateShort: s => s ? new Date(s+"T12:00:00").toLocaleDateString("es-MX",{day:"2-digit",month:"short"}) : "—",
  // DD/MM/AA: la forma canónica en tablas y rieles densos (fechas en columna,
  // ancho fijo, sin nombres de mes que rompan el alineado).
  dateNum: s => s ? `${s.slice(8,10)}/${s.slice(5,7)}/${s.slice(2,4)}` : "—",
  pct: (a,b) => b > 0 ? `${Math.round((a/b)*100)}%` : "0%",
  // Timestamps del laboratorio (criterio 17025): SIEMPRE en la zona del
  // laboratorio (America/Mazatlan), no en la del navegador. El dato se
  // guarda en UTC (timestamptz del server); esto es solo presentación.
  ts: (s, opts) => s ? new Date(s).toLocaleString("es-MX", {
    timeZone: "America/Mazatlan",
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
    ...(opts || {}),
  }) : "—",
};

const daysBetween = (d1, d2) => {
  if (!d1 || !d2) return null;
  return Math.floor((new Date(d2) - new Date(d1)) / 86400000);
};

// El día LOCAL de hoy, calculado al momento de pedirlo. Es lo que se escribe
// en la base, se imprime en un documento o nombra un archivo. NUNCA
// `new Date().toISOString().slice(0, 10)`: eso es el día UTC, y en La Paz
// (UTC-7) después de las 5 de la tarde ya es mañana (regla de lint).
const hoyLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// `today` es un binding VIVO de ES modules: este módulo lo reasigna cuando el
// día cambia y quien lo importa lee el valor nuevo en su siguiente lectura.
// Hasta 2026-09-11 era una constante calculada al cargar la app, y una tablet
// que se quedaba abierta de un día para otro capturaba con la fecha de ayer
// (lo usan unos 80 valores iniciales en 53 archivos). Se revisa al volver a
// la pestaña y ANTES de que un toque o una tecla lleguen a su destino (fase
// de captura), así la acción que dispara el usuario ya ve el día correcto.
// Un formulario que ya estaba abierto conserva el valor con que nació.
let today = hoyLocal();
const refrescarHoy = () => { const h = hoyLocal(); if (h !== today) today = h; };
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', refrescarHoy);
  window.addEventListener('focus', refrescarHoy);
  window.addEventListener('pageshow', refrescarHoy);
  document.addEventListener('pointerdown', refrescarHoy, { capture: true, passive: true });
  document.addEventListener('keydown', refrescarHoy, true);
}

// El día LOCAL de un instante (timestamptz / ISO). Cortar el ISO con
// slice(0,10) daría el día UTC: un hecho registrado a las 18:00 en La Paz
// (UTC-7) se leería como del día siguiente. El día que ve el usuario es el
// suyo, y `today` de arriba también es local: los dos lados del mismo reloj.
// `today` se refresca con la actividad del usuario; una pantalla que solo
// MIRA (el Inicio en un monitor que nadie toca) necesita además re-pintarse
// cuando el día cambia: este hook revisa cada minuto y al volver a la
// pestaña, y solo avisa cuando el día CAMBIA (el updater funcional devuelve
// el mismo string y React no re-renderiza).
const useHoyVivo = () => {
  const [hoy, setHoy] = useState(today);
  useEffect(() => {
    const revisar = () => setHoy(prev => {
      const iso = hoyLocal();
      return iso === prev ? prev : iso;
    });
    const id = setInterval(revisar, 60000);
    document.addEventListener('visibilitychange', revisar);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', revisar); };
  }, []);
  return hoy;
};

// ── Buscar como se teclea ─────────────────────────────────────
// Texto plegado (minúsculas, sin acentos) y ¿este registro coincide con lo
// tecleado? Salen del motor único de búsqueda (busqueda.logic.js, 2026-09-18):
// cada palabra donde empieza una palabra, las vacías ("de", "la") no se exigen
// solas y entre comillas solo vale la frase exacta. Sin consulta, todo coincide.
// Quien dibuja una lista usa también buscarEn y gruposDeBusqueda para ordenar
// por nivel y decirlo con las mismas palabras.

const diaLocal = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ══════════════════════════════════════════════
//  HOOK: DETECCIÓN DE DISPOSITIVO
//  Escucha el ancho de ventana en tiempo real.
//  < 768px  → móvil/tablet  (isMobile = true)
//  ≥ 768px  → escritorio    (isMobile = false)
//  Se recalcula automáticamente al rotar o
//  redimensionar — nunca afecta escritorio.
// ══════════════════════════════════════════════
const MOBILE_BP = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BP);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BP - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

// ══════════════════════════════════════════════
//  HOOK: BANDA DE TABLET (768 – 1099 px)
//  El layout de escritorio CABE pero va apretado:
//  tablet de campo en portrait (800px), ventana a
//  media pantalla. Es la banda donde no aplica
//  ninguna @media del proyecto y isMobile ya es
//  false. Antes solo la reconocian dos pseudo-
//  hooks clonados en los tabs de Despacho, con el
//  umbral 1100 escrito a mano. Aqui vive una vez.
//  Mide con matchMedia en el estado inicial Y en
//  el listener: una sola condicion, sin deriva.
// ══════════════════════════════════════════════
const TABLET_BP = 1100;
const MQ_TABLET = `(min-width: ${MOBILE_BP}px) and (max-width: ${TABLET_BP - 1}px)`;

function useEsTablet() {
  const [esTablet, setEsTablet] = useState(() => window.matchMedia(MQ_TABLET).matches);
  useEffect(() => {
    const mq = window.matchMedia(MQ_TABLET);
    const handler = (e) => setEsTablet(e.matches);
    setEsTablet(mq.matches);   // reconcilia por si el ancho cambio antes del efecto
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return esTablet;
}

// ══════════════════════════════════════════════
//  HOOK: BANDA ANGOSTA (< 1100 px)
//  Movil O tablet: todo ancho donde el layout
//  de escritorio no cabe comodo. Es el switch
//  de "esto tiene que ceder" (podar etiquetas,
//  colapsar un boton a icono, soltar adornos)
//  sin decidir todavia CUAL de las dos formas
//  se aplica; para eso estan isMobile/esTablet.
// ══════════════════════════════════════════════
const MQ_ANGOSTO = `(max-width: ${TABLET_BP - 1}px)`;

function useEsAngosto() {
  const [angosto, setAngosto] = useState(() => window.matchMedia(MQ_ANGOSTO).matches);
  useEffect(() => {
    const mq = window.matchMedia(MQ_ANGOSTO);
    const handler = (e) => setAngosto(e.matches);
    setAngosto(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return angosto;
}

// ══════════════════════════════════════════════
//  HOOK: EL TELÉFONO EN HORIZONTAL (alto corto, 2026-10-29)
//  Ancho de escritorio (768 o más) con poco alto y
//  un dedo como puntero: el teléfono acostado. Ahí
//  el ALTO es lo escaso y el cascarón se compacta
//  (lienzo "GEOBAJA en horizontal"). El puntero deja
//  fuera una ventana de escritorio achicada. La misma
//  consulta la repite public/tema.js antes de React
//  y la escribe <html data-alto="corto"> (Apariencia).
// ══════════════════════════════════════════════
const MQ_ALTO_CORTO = `(min-width: ${MOBILE_BP}px) and (max-height: 520px) and (pointer: coarse)`;

function useAltoCorto() {
  const [corto, setCorto] = useState(() => !!window.matchMedia?.(MQ_ALTO_CORTO).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(MQ_ALTO_CORTO);
    if (!mq) return undefined;
    const handler = (e) => setCorto(e.matches);
    setCorto(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return corto;
}

// ══════════════════════════════════════════════
//  HOOK: DISPOSITIVO TÁCTIL (pointer: coarse)
//  True cuando el puntero PRIMARIO es un dedo
//  (tablet/celular). Una laptop con touchscreen
//  pero mouse/trackpad primario da false. Cubre
//  la brecha de la tablet de campo (800px de
//  ancho ≥ MOBILE_BP pero operación táctil).
// ══════════════════════════════════════════════
function useEsTactil() {
  const [tactil, setTactil] = useState(() => window.matchMedia("(pointer: coarse)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    const handler = (e) => setTactil(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return tactil;
}

// ══════════════════════════════════════════════
//  HOOK: TAMAÑO REAL DEL VIEWPORT (visualViewport)
//  Devuelve el área VISIBLE real de la pestaña en px
//  (width/height/offsetTop/offsetLeft). A diferencia de
//  vh/dvh, considera la barra del navegador móvil y el
//  teclado en pantalla → permite ocupar el alto exacto
//  sin "juego" ni recortes. Fallback a innerWidth/Height.
// ══════════════════════════════════════════════
function _leerViewport() {
  const vv = (typeof window !== "undefined") ? window.visualViewport : null;
  if (vv) return { width: Math.round(vv.width), height: Math.round(vv.height), offsetTop: vv.offsetTop || 0, offsetLeft: vv.offsetLeft || 0 };
  return { width: window.innerWidth, height: window.innerHeight, offsetTop: 0, offsetLeft: 0 };
}

function useVisualViewport() {
  const [vp, setVp] = useState(_leerViewport);
  useEffect(() => {
    let raf = null;
    const onChange = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const next = _leerViewport();
        setVp(prev => (prev.width === next.width && prev.height === next.height && prev.offsetTop === next.offsetTop && prev.offsetLeft === next.offsetLeft) ? prev : next);
      });
    };
    window.addEventListener("resize", onChange);
    const vv = window.visualViewport;
    if (vv) { vv.addEventListener("resize", onChange); vv.addEventListener("scroll", onChange); }
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onChange);
      if (vv) { vv.removeEventListener("resize", onChange); vv.removeEventListener("scroll", onChange); }
    };
  }, []);
  return vp;
}

// Comparador cronológico para meses formato "ENE 2025", "FEB 2025", etc.
const ORDEN_MES = {ENE:1,FEB:2,MAR:3,ABR:4,MAY:5,JUN:6,JUL:7,AGO:8,SEP:9,OCT:10,NOV:11,DIC:12};

// `cobros.mes` en el ÚNICO formato del proyecto: "AGO 2026". Es el derivador
// único de esa columna — antes lo calculaban cinco escritores y dos de ellos
// guardaban ISO ("2026-08"), así que la misma columna hablaba dos idiomas y
// `sortMeses` no podía ordenarla. Deriva SIEMPRE de `periodo_hasta`: el mes de
// una estimación es el del trabajo ejecutado, no el de su captura.
const MESES_CORTOS = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];
// La columna acepta NULL o esa grafía exacta (CHECK `cobros_mes_formato`,
// año de CUATRO dígitos). Ante una fecha que no puede expresar así devuelve
// null, que la base sí admite: hasta 2026-09-15 devolvía '', la única cadena que
// el CHECK rechaza, y un año tecleado corto ("SEP 26") tumbaba el alta con
// "Los datos no cumplen con las reglas de validación", sin decir qué campo.
// Sin mes, no un mes inventado: quien captura lo ataja antes con `fechaPlausible`.
const mesCorto = iso => {
  if (!iso) return null;
  const d = new Date(iso + 'T12:00:00');
  if (Number.isNaN(d.getTime())) return null;
  const anio = d.getFullYear();
  if (anio < 1000 || anio > 9999) return null;
  return `${MESES_CORTOS[d.getMonth()]} ${anio}`;
};

// El año de un `<input type="date">` se TECLEA: escribir "26" deja 0026, que
// Postgres acepta como fecha y por eso llegaba hasta la base. Estas dos leen la
// fecha capturada para que la pantalla la rechace con nombre y apellido.
// El rango es el de trabajo de la empresa, no el que el tipo date permite.
const ANIO_MIN_CAPTURA = 2000;
const ANIO_MAX_CAPTURA = 2100;
const anioDe = iso => {
  if (!iso) return null;
  const d = new Date(iso + 'T12:00:00');
  return Number.isNaN(d.getTime()) ? null : d.getFullYear();
};
const fechaPlausible = iso => {
  const a = anioDe(iso);
  return a !== null && a >= ANIO_MIN_CAPTURA && a <= ANIO_MAX_CAPTURA;
};
const sortMeses = (a, b) => {
  if (!a || !b) return 0;
  const [mA, yA] = a.split(" "); const [mB, yB] = b.split(" ");
  return yA !== yB ? parseInt(yA) - parseInt(yB) : (ORDEN_MES[mA]||0) - (ORDEN_MES[mB]||0);
};

const getPeriodicidadDias = p => ({ "Semanal":7,"Quincenal":15,"Mensual":30,"Bimestral":60 }[p] || null);

// ── Días de crédito: cuánto tiene el cliente para pagar la FACTURA ─────────
//  Son 30 por omisión, y no salen de la periodicidad de la obra. La cadencia
//  con que se ESTIMA y el plazo que se le da al cliente para PAGAR son dos
//  hechos distintos que compartían una sola columna: un contrato semanal le
//  regalaba 7 días de crédito y su factura entraba a cartera vencida en una
//  semana; uno bimestral, 60. El crédito corre desde la fecha de factura, nunca
//  desde el envío ni desde la aprobación (pasos.js).
//  Decisión del dueño (2026-09-13): 30 días, configurables en el panel.
//  Si algún día hace falta crédito por cliente o por contrato, la cascada va
//  DENTRO de esta función (contrato → obra → cliente → configuración global);
//  nunca una columna nueva en `cobros`, que es espejo de sus facturas hijas.
const DIAS_CREDITO_DEFAULT = 30;
const diasCredito = (adminConfig) => {
  const n = Number(adminConfig?.dias_credito);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : DIAS_CREDITO_DEFAULT;
};

// Suma correcta por periodicidad: mensual/bimestral usan meses calendario, semanal/quincenal usan días exactos
// ══════════════════════════════════════════════
//  LÓGICA DE PERÍODOS DE ESTIMACIÓN (legado mensual)
//  Fallback del motor único de períodos (sugerirPeriodo en
//  presupuestos/arranque.js) — los módulos consumen aquel, no este.
//  Soporta 3 tipos según obra.tipo_periodo:
//  - mes_completo : del 1 al último del mes
//  - dia_ancla    : del día N al día N-1 del siguiente mes
//  - semana_laboral: 4 semanas lunes-sábado (cierre sábado de la 4ª semana)
// ══════════════════════════════════════════════

// Dado una obra y los cobros existentes, calcula el período siguiente {desde, hasta}
const getPeriodoSiguiente = (obra, cobros = []) => {
  if (!obra.fecha_inicio || obra.estatus === "Cerrada") return null;
  if (obra.periodicidad === "Por hito") return null;

  // Buscar el periodo_hasta más reciente de esta obra (un período invertido,
  // desde > hasta, es dato sucio y no ancla nada)
  const ultimaEst = cobros
    .filter(c => c.obra_id === obra.id && c.periodo_hasta && (!c.periodo_desde || c.periodo_hasta >= c.periodo_desde))
    .sort((a, b) => new Date(b.periodo_hasta) - new Date(a.periodo_hasta))[0] || null;

  const tipo = obra.tipo_periodo || "mes_completo";
  const ancla = obra.dia_ancla || 1;

  // ── MES COMPLETO: del 1 al último del mes ──
  if (tipo === "mes_completo") {
    let base;
    if (ultimaEst) {
      // El siguiente mes al del último período
      const d = new Date(ultimaEst.periodo_hasta + "T12:00:00");
      d.setDate(1);
      d.setMonth(d.getMonth() + 1);
      base = d;
    } else {
      // Sin estimaciones: usar mes de fecha_inicio
      base = new Date(obra.fecha_inicio + "T12:00:00");
      base.setDate(1);
    }
    const desde = base.toISOString().split("T")[0];
    const hasta = new Date(base.getFullYear(), base.getMonth() + 1, 0)
      .toISOString().split("T")[0];
    return { desde, hasta };
  }

  // ── DÍA ANCLA: del día N al día N-1 del siguiente mes ──
  if (tipo === "dia_ancla") {
    let desdeDate;
    if (ultimaEst) {
      // El día ancla del mes siguiente al del último periodo_hasta
      const d = new Date(ultimaEst.periodo_hasta + "T12:00:00");
      // periodo_hasta es ancla-1, así que sumamos 1 día para obtener la nueva ancla
      desdeDate = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    } else {
      // Sin estimaciones: ancla del mes de fecha_inicio
      const fi = new Date(obra.fecha_inicio + "T12:00:00");
      desdeDate = new Date(fi.getFullYear(), fi.getMonth(), ancla);
    }
    // hasta = desdeDate + 1 mes - 1 día
    const hastaDate = new Date(desdeDate.getFullYear(), desdeDate.getMonth() + 1, desdeDate.getDate() - 1);
    return {
      desde: desdeDate.toISOString().split("T")[0],
      hasta: hastaDate.toISOString().split("T")[0],
    };
  }

  // ── SEMANA LABORAL: 4 semanas lun-sáb desde el lunes siguiente al último cierre ──
  if (tipo === "semana_laboral") {
    let inicioDate;
    if (ultimaEst) {
      // Lunes siguiente al último cierre (normalmente sábado; robusto si el
      // último período cerró en otro día)
      const d = new Date(ultimaEst.periodo_hasta + "T12:00:00");
      d.setDate(d.getDate() + 1);
      while (d.getDay() !== 1) d.setDate(d.getDate() + 1);
      inicioDate = d;
    } else {
      // Sin estimaciones: buscar el lunes más cercano a fecha_inicio
      const fi = new Date(obra.fecha_inicio + "T12:00:00");
      const dow = fi.getDay(); // 0=dom,1=lun...6=sáb
      const diasHastaLunes = dow === 1 ? 0 : dow === 0 ? 1 : (8 - dow) % 7;
      inicioDate = new Date(fi.getFullYear(), fi.getMonth(), fi.getDate() + diasHastaLunes);
    }
    // hasta = inicio + 26 días (sábado de la 4ª semana, cierre L-S)
    const hastaDate = new Date(inicioDate.getFullYear(), inicioDate.getMonth(), inicioDate.getDate() + 26);
    return {
      desde: inicioDate.toISOString().split("T")[0],
      hasta: hastaDate.toISOString().split("T")[0],
    };
  }

  return null;
};

// Compatibilidad: devuelve solo la fecha "próxima" (desde) para el calendario
const getProximaEstimacion = (obra, cobros = []) => {
  const p = getPeriodoSiguiente(obra, cobros);
  return p ? p.desde : null;
};

// Legado: addPeriodicidad solo se usa ya en NuevaEstimacionModal para sugerir periodo_hasta
const addPeriodicidad = (fechaStr, periodicidad) => {
  const d = new Date(fechaStr + "T12:00:00");
  switch(periodicidad) {
    case "Semanal":    d.setDate(d.getDate() + 7);   break;
    case "Quincenal":  d.setDate(d.getDate() + 15);  break;
    case "Mensual":    d.setMonth(d.getMonth() + 1); break;
    case "Bimestral":  d.setMonth(d.getMonth() + 2); break;
    default: return null;
  }
  return d.toISOString().split("T")[0];
};

function newId(prefix, arr) {
  const nums = arr.map(x => parseInt(x.id.replace(prefix+"-",""))).filter(n => !isNaN(n));
  const max = nums.length ? Math.max(...nums) : 0;
  return `${prefix}-${String(max+1).padStart(3,"0")}`;
}

// ══════════════════════════════════════════════
//  EL ID DE UN COBRO CUELGA DE SU OBRA (2026-09-17, decisión del dueño:
//  "la numeración y etiquetado debería ser por obra, no global; así nunca
//  chocaría con la numeración general puesto que la estimación consecutiva de
//  una obra nunca se empalma con otra").
//
//  Un consecutivo GLOBAL no se puede calcular desde el navegador: RLS acota los
//  cobros por obra, así que un rol acotado ve un máximo más chico que el real y
//  pide un id ya ocupado. Caso medido (2026-09-17): Felix, ingeniero_costos_jr
//  con 4 obras, veía hasta COB-193 con COB-197 ya emitido, y toda alta suya
//  moría con "Ya existe un registro con esos datos" (23505 contra cobros_pkey).
//  Por obra sí se puede: RLS filtra POR obra y nunca DENTRO de una, y
//  cobros_insert solo deja escribir en obras asignadas, así que quien puede
//  crear el cobro ve la rama completa.
//
//  El consecutivo es de FILAS de la obra, NO `numero_estimacion`: ese folio es
//  por CONTRATO (OBR-022 tiene once estimaciones "#1", una por cada contrato),
//  es nulo en los sin actividad y se puede reeditar — y una llave primaria de
//  la que cuelgan facturas, pagos, partidas, custodia, fotos y generadores no
//  cambia nunca. La numeración que el usuario ve sigue donde estaba.
//
//  Los ids del esquema viejo (COB-136) siguen siendo válidos y no estorban: son
//  otro formato (COB-030-001 nunca es igual a COB-NNN), así que la rama de cada
//  obra empieza en 001 y no hay nada que migrar. Sin obra (solo puede pasarle a
//  quien ve todo, porque el acotado tiene que declararla) se cae al global.
// ══════════════════════════════════════════════
function idCobroDeObra(obraId, cobros = []) {
  if (!obraId) return newId("COB", cobros);
  const rama = `COB-${String(obraId).replace(/^OBR-/, "")}-`;
  const nums = cobros
    .filter(c => typeof c?.id === "string" && c.id.startsWith(rama))
    .map(c => parseInt(c.id.slice(rama.length), 10))
    .filter(n => !isNaN(n));
  const max = nums.length ? Math.max(...nums) : 0;
  return `${rama}${String(max + 1).padStart(3, "0")}`;
}

// ══════════════════════════════════════════════
//  COMPONENTES BASE
// ══════════════════════════════════════════════

// ══════════════════════════════════════════════
//  COBERTURA DE SERVICIO — Iguala / semana_laboral
//  Calcula hasta qué fecha alcanza el presupuesto disponible.
//  Requiere presupuesto.monto_autorizado (total contrato) y
//  presupuesto.monto_total (tasa mensual de iguala).
// ══════════════════════════════════════════════
const calcCoberturaServicio = (presupuesto, cobros = []) => {
  if (!['Iguala', 'Mixto'].includes(presupuesto.tipo_contrato)) return null;

  const montoMensual    = presupuesto.monto_total     || 0;
  // Autorizado vigente = contrato base + addendums/órdenes de cambio. Base con el
  // MISMO fallback que el techo del resto de la app (y que calcAmortizacionAnticipo):
  // monto_autorizado>0, si no monto_total — sin él, un contrato capturado por
  // conceptos (monto_autorizado=0, p.ej. Mixto) reportaba "sin saldo" con disponible.
  const montoAddendums  = (presupuesto.addendums || []).reduce((s, a) => s + (parseFloat(a.monto_adicional) || 0), 0);
  const baseAutorizada  = (presupuesto.monto_autorizado > 0 ? presupuesto.monto_autorizado : presupuesto.monto_total) || 0;
  const montoAutorizado = baseAutorizada + montoAddendums;
  if (montoMensual <= 0 || montoAutorizado <= 0) return null;

  const estCobros   = cobros.filter(c => c.presupuesto_id === presupuesto.id && c.estatus_cobro !== 'CANCELADA');
  const totalEjerc  = estCobros.reduce((s, c) => s + (c.monto || 0), 0);
  const pctEjerc    = (totalEjerc / montoAutorizado) * 100;
  const montoDisp   = Math.max(0, montoAutorizado - totalEjerc);
  const mesesDisp   = montoDisp / montoMensual;

  // Último periodo_hasta cobrado
  const ultCobro  = estCobros
    .filter(c => c.periodo_hasta)
    .sort((a, b) => new Date(b.periodo_hasta) - new Date(a.periodo_hasta))[0] || null;
  const ultPeriodo = ultCobro?.periodo_hasta || null;

  // Proyección de fecha de cobertura (L-S, sin domingos)
  let fechaCobertura = null;
  if (ultPeriodo && mesesDisp > 0) {
    const tipo = presupuesto.tipo_periodo || 'mes_completo';
    if (tipo === 'semana_laboral') {
      // Inicio: lunes siguiente al sábado del último período (+2 días)
      const lastSat = new Date(ultPeriodo + 'T12:00:00');
      const nextMon = new Date(lastSat);
      nextMon.setDate(nextMon.getDate() + 2);

      // Días disponibles: 1 mes = 4 sem × 7 días cal → floor(mesesDisp × 28)
      // Contar días no-domingo desde nextMon
      const diasDisp = Math.floor(mesesDisp * 28);
      let cursor = new Date(nextMon);
      cursor.setDate(cursor.getDate() - 1); // arrancar antes del primer día
      let count = 0;
      while (count < diasDisp) {
        cursor.setDate(cursor.getDate() + 1);
        if (cursor.getDay() !== 0) count++; // contar solo L-S
      }
      fechaCobertura = cursor.toISOString().split('T')[0];
    } else {
      // mes_completo / dia_ancla: 1 mes ≈ 30 días calendario
      const d = new Date(ultPeriodo + 'T12:00:00');
      d.setDate(d.getDate() + Math.floor(mesesDisp * 30));
      fechaCobertura = d.toISOString().split('T')[0];
    }
  }

  // Tope de vigencia: la proyección dinero÷tasa no puede rebasar el fin del
  // contrato. Si el saldo alcanza más allá de fecha_fin, la cobertura real es
  // "todo lo que resta del contrato" — estado bueno, no alarma.
  let cubreFinContrato = false;
  if (fechaCobertura && presupuesto.fecha_fin && fechaCobertura > presupuesto.fecha_fin) {
    fechaCobertura   = presupuesto.fecha_fin;
    cubreFinContrato = true;
  }

  const diasHasta = fechaCobertura ? daysBetween(today, fechaCobertura) : null;
  const alertaNivel =
    montoDisp <= 0               ? 'agotado' :
    cubreFinContrato             ? 'ok'      :
    diasHasta != null && diasHasta <= 14 ? 'danger'  :
    diasHasta != null && diasHasta <= 30 ? 'warning' : 'ok';

  return { montoDisponible: montoDisp, mesesDisponibles: mesesDisp, pctEjerc, fechaCobertura, ultPeriodo, alertaNivel, diasHasta, cubreFinContrato };
};

// ══════════════════════════════════════════════
//  CALCULADORA DE ORDEN DE CAMBIO
//  Dado un período (inicio → fin) y la tasa mensual,
//  calcula meses, semanas y monto de la OC.
//  Usa días laborales L-S (excluye domingos).
//  1 mes = 4 semanas = 24 días laborales.
// ══════════════════════════════════════════════
const calcOrdenCambio = (fechaInicio, fechaFin, montoMensual) => {
  if (!fechaInicio || !fechaFin || !montoMensual) return null;
  const start = new Date(fechaInicio + 'T12:00:00');
  const end   = new Date(fechaFin   + 'T12:00:00');
  if (end < start) return null;

  // Contar días laborales L-S inclusive
  let diasLab = 0;
  const cur = new Date(start);
  while (cur <= end) {
    if (cur.getDay() !== 0) diasLab++;
    cur.setDate(cur.getDate() + 1);
  }

  const semanas  = diasLab / 6;           // 6 días lab/semana
  const meses    = diasLab / 24;          // 24 días lab/mes
  const montoNeto  = meses * montoMensual;
  const montoIva   = montoNeto * 0.16;
  const montoTotal = montoNeto + montoIva;

  return { diasLab, semanas, meses, montoNeto, montoIva, montoTotal };
};

// ══════════════════════════════════════════════
//  IMPORTE CON LETRA: UN motor para todo documento (la estimación y su carátula en
//  pantalla, la Mesa, la estimación del contratista, el presupuesto, la carta y las
//  tarjetas de PU). Ej: 1234.50 → "MIL DOSCIENTOS TREINTA Y CUATRO PESOS 50/100 M.N."
//  Grafía (2026-10-24, observación de un cliente sobre la carátula de control de
//  calidad, que decía "VEINTE Y SEIS MIL"): del 16 al 29 UNA palabra con su tilde
//  (DIECISÉIS, VEINTISÉIS); apócope ante MIL, MILLÓN y la moneda (VEINTIÚN MIL,
//  TREINTA Y UN PESOS, UN PESO); el millón exacto lleva DE (UN MILLÓN DE PESOS). Los
//  centavos salen del importe redondeado a centavos (10.999 es ONCE PESOS 00/100,
//  nunca DIEZ PESOS 100/100). Lo que no es un número no se afirma: '-'.
//  'moneda' y 'sufijo' los pone el manifiesto de la estimación; lo demás imprime
//  PESOS y M.N.
// ══════════════════════════════════════════════
const LETRAS_0_29 = ['CERO', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE', 'DIEZ',
  'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE', 'VEINTE',
  'VEINTIÚN', 'VEINTIDÓS', 'VEINTITRÉS', 'VEINTICUATRO', 'VEINTICINCO', 'VEINTISÉIS', 'VEINTISIETE', 'VEINTIOCHO',
  'VEINTINUEVE'];
const LETRAS_DECENAS = ['', '', '', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
const LETRAS_CENTENAS = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS',
  'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];
// 1 a 999, siempre delante de un sustantivo (MIL, MILLÓN o la moneda): por eso UN y VEINTIÚN.
const letrasHasta999 = (x) => {
  if (x === 100) return 'CIEN';
  const c = Math.floor(x / 100), r = x % 100;
  const resto = r === 0 ? ''
    : r < 30 ? LETRAS_0_29[r]
    : LETRAS_DECENAS[Math.floor(r / 10)] + (r % 10 ? ` Y ${LETRAS_0_29[r % 10]}` : '');
  return [LETRAS_CENTENAS[c], resto].filter(Boolean).join(' ');
};
const enteroEnLetras = (n) => {
  if (n < 1000) return letrasHasta999(n);
  if (n < 1e6) {
    const miles = Math.floor(n / 1000), r = n % 1000;
    return [miles === 1 ? 'MIL' : `${letrasHasta999(miles)} MIL`, r ? letrasHasta999(r) : ''].filter(Boolean).join(' ');
  }
  const mill = Math.floor(n / 1e6), r = n % 1e6;
  return [mill === 1 ? 'UN MILLÓN' : `${enteroEnLetras(mill)} MILLONES`, r ? enteroEnLetras(r) : ''].filter(Boolean).join(' ');
};
const monedaSingular = (moneda) => moneda.replace(/ES$/, '').replace(/S$/, '');
const numeroALetras = (num, { moneda = 'PESOS', sufijo = 'M.N.' } = {}) => {
  const n = Number(num);
  if (num == null || num === '' || !Number.isFinite(n)) return '-';
  const cents = Math.round((Math.abs(n) + Number.EPSILON) * 100);
  const entero = Math.floor(cents / 100), centavos = cents % 100;
  const letras = entero === 0 ? 'CERO' : enteroEnLetras(entero);
  const de = entero >= 1e6 && entero % 1e6 === 0 ? ' DE' : '';
  const unidad = entero === 1 ? monedaSingular(moneda) : moneda;
  return `${letras}${de} ${unidad} ${String(centavos).padStart(2, '0')}/100 ${sufijo}`.trim();
};

// ══════════════════════════════════════════════
//  AMORTIZACIÓN DE ANTICIPO — sugerencia por estimación
//  Proporcional: (anticipo ÷ total del contrato) × importe s/IVA de la
//  estimación, topado al saldo de anticipo aún no amortizado. El usuario
//  puede sobrescribir el monto (p. ej. amortizar todo antes de tiempo).
//  anticipo_pct se interpreta como % del contrato destinado a anticipo.
//  cobrosPrevios = estimaciones anteriores del MISMO presupuesto (sin la actual).
// ══════════════════════════════════════════════
const r2 = n => Math.round((n + Number.EPSILON) * 100) / 100;
const calcAmortizacionAnticipo = (presupuesto, montoSIVA, cobrosPrevios = []) => {
  const pctRaw = parseFloat(presupuesto?.anticipo_pct) || 0;
  const monRaw = parseFloat(presupuesto?.anticipo_monto) || 0;
  const addend = (presupuesto?.addendums || []).reduce((s, a) => s + (parseFloat(a.monto_adicional) || 0), 0);
  const base   = (parseFloat(presupuesto?.monto_autorizado) > 0
                    ? parseFloat(presupuesto.monto_autorizado)
                    : parseFloat(presupuesto?.monto_total)) || 0;
  const totalContrato    = base + addend;
  // El monto en pesos manda cuando existe (exacto); si no, se deriva del %.
  const anticipoTotal    = monRaw > 0 ? r2(monRaw) : r2(totalContrato * pctRaw / 100);
  const antPct           = totalContrato > 0 ? r2(anticipoTotal / totalContrato * 100) : pctRaw;
  const rate             = totalContrato > 0 ? anticipoTotal / totalContrato : 0;
  const amortizadoPrevio = r2(cobrosPrevios.reduce((s, c) => s + (parseFloat(c.amortizacion_monto) || 0), 0));
  const saldo            = Math.max(0, r2(anticipoTotal - amortizadoPrevio));
  const proporcional     = r2((parseFloat(montoSIVA) || 0) * rate);
  const sugerida         = Math.min(proporcional, saldo);
  return { antPct, anticipoTotal, amortizadoPrevio, saldo, sugerida };
};

// ══════════════════════════════════════════════
//  DESGLOSE FINANCIERO DE UNA ESTIMACIÓN (con deducciones)
//  Fuente única del "líquido a pagar". Trata el IVA del anticipo:
//   • Anticipo SIN IVA  → la amortización se descuenta del importe ANTES de IVA
//     (reduce la base gravable; el IVA se recalcula sobre el neto).
//   • Anticipo CON IVA  → la amortización se descuenta del total c/IVA.
//  Las retenciones siempre se descuentan del total (son retención sobre el pago).
//  No altera monto/monto_iva (el trabajo facturado); solo deriva el líquido.
// ══════════════════════════════════════════════
const calcDesgloseEstimacion = (cobro, presupuesto) => {
  const X  = parseFloat(cobro?.monto) || 0;                 // importe s/IVA del trabajo
  const a  = parseFloat(cobro?.amortizacion_monto) || 0;    // amortización (sin IVA)
  const rG = parseFloat(cobro?.retencion_garantia) || 0;
  const rM = parseFloat(cobro?.retencion_multa) || 0;
  const ivaRate   = presupuesto?.iva_exento ? 0 : 0.16;
  const antConIva = !!presupuesto?.anticipo_con_iva;

  let baseGravable, iva, totalConIva, importeNeto;
  if (antConIva) {
    baseGravable = X;
    iva          = r2(X * ivaRate);
    totalConIva  = r2(X + iva);
    importeNeto  = r2(totalConIva - a - rG - rM);
  } else {
    baseGravable = r2(X - a);
    iva          = r2(baseGravable * ivaRate);
    totalConIva  = r2(baseGravable + iva);
    importeNeto  = r2(totalConIva - rG - rM);
  }
  return {
    importeSIVA: X, amortizacion: a, retGarantia: rG, retMulta: rM, antConIva,
    baseGravable, iva, totalConIva, importeNeto,
    hayDeducciones: a > 0 || rG > 0 || rM > 0,
  };
};

// Total CON IVA de un cobro, tolerante a las DOS semánticas que la columna
// monto_iva tuvo históricamente: la canónica (total c/IVA, siempre >= monto;
// con IVA exento son iguales) y la legada del modal de cobro directo (solo el
// impuesto, siempre < monto). Ningún lector de dinero debe sumar monto_iva a
// secas ni hacer monto + monto_iva: la primera forma subestimaba los cobros
// legados y la segunda duplicaba los canónicos.
const totalConIvaCobro = (c) => {
  const monto = Number(c?.monto) || 0;
  const mi = Number(c?.monto_iva) || 0;
  return mi >= monto ? mi : monto + mi;
};

// % de avance con redondeo HONESTO (ley de datos duros): floor a 1 decimal
// — nunca proclamar 100% antes de que el numerador cubra al denominador,
// nunca 0% con avance positivo, y a un centavo del tope se dice >99.9%,
// no 99.9%. Un excedente se declara (>100), no se satura. Criterio ÚNICO
// para el mismo hecho en banda, documento y cualquier superficie nueva:
// los operandos deben venir redondeados a centavos (r2) antes de dividir.
const pctTexto = (p) => {
  if (p == null) return null;
  if (p > 0 && p < 0.1) return '<0.1%';
  if (p > 99.9 && p < 100) return '>99.9%';
  if (p > 100 && p < 100.1) return '>100%';   // el excedente chico también se declara
  const f = Math.floor(p * 10) / 10;
  return `${f % 1 === 0 ? f.toFixed(0) : f.toFixed(1)}%`;
};

// ── Importes buscables (cartera, lista de presupuestos y Spotlight) ──────────
//  Un importe se encuentra tecleado como sale en el CFDI ("$97,904.00"), como
//  número pelón ("97904") o con separadores ("97,904"): se indexan las tres
//  grafías y el buscador de texto hace el resto. Con y sin IVA se indexan
//  aparte: la contadora busca por lo que dice la factura.
const IVA_PCT = 0.16;
const montoBuscable = (n) => {
  if (n == null || n === '') return '';
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  return `${v.toFixed(2)} ${Math.round(v)} ${fmt.money(v)}`;
};
// Importe c/IVA a partir del s/IVA, a centavos; null sin importe. `exento`
// (presupuestos.iva_exento) deja el importe tal cual.
const conIva = (n, exento = false) => {
  if (n == null || n === '') return null;
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return exento ? r2(v) : r2(v * (1 + IVA_PCT));
};

export {
  IVA_PCT, montoBuscable, conIva,
  fmt, daysBetween, today, hoyLocal, diaLocal, useHoyVivo, plegar, coincide, MOBILE_BP, TABLET_BP, useIsMobile, useEsTablet,
  useEsAngosto, useEsTactil, useVisualViewport, MQ_ALTO_CORTO, useAltoCorto,
  ORDEN_MES, sortMeses, mesCorto, anioDe, fechaPlausible, ANIO_MIN_CAPTURA, ANIO_MAX_CAPTURA,
  getPeriodicidadDias, diasCredito, DIAS_CREDITO_DEFAULT,
  getPeriodoSiguiente, getProximaEstimacion, addPeriodicidad, newId, idCobroDeObra,
  calcCoberturaServicio, calcOrdenCambio, numeroALetras,
  calcAmortizacionAnticipo, calcDesgloseEstimacion, totalConIvaCobro,
  r2, pctTexto,
};
